import { useState } from "react";
import { Smartphone, Copy, Check, Radio } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SECTIONS } from "@/lib/sections";

/**
 * MobileBroadcastPanel — editor-only.
 * Generates a Mux RTMPS URL + stream key for the editor to paste into Larix.
 * Once paired, Larix streams from the iPhone to Mux directly; our server
 * doesn't sit in the path (unlike the browser broadcaster).
 */
export const MobileBroadcastPanel = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftSection, setDraftSection] = useState("art-de-vivre");
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCombined, setCopiedCombined] = useState(false);
  const [copiedAlt, setCopiedAlt] = useState(false);

  const generate = async () => {
    if (!draftTitle.trim()) return toast.error("Give your broadcast a title.");
    setBusy(true);
    try {
      const { data } = await api.post("/live/start", {
        title: draftTitle.trim(),
        section: draftSection,
        source: "mobile",
      });
      setCreds(data);
      toast.success("Stream key generated. Paste it into Larix on your phone.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate stream key.");
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text, setFlag) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlag(true);
      setTimeout(() => setFlag(false), 1500);
    } catch {
      toast.error("Copy failed — long-press to copy manually.");
    }
  };

  const finish = async (save) => {
    if (!creds) return;
    setBusy(true);
    try {
      await api.post(`/live/${creds.id}/finish`, {
        title: creds.title,
        section: creds.section,
        save_to_archive: save,
        show_on_profile: save,
        post_to_room: false,
      });
      if (!save) {
        await api.delete(`/live/${creds.id}`);
      }
      toast.success(save ? "Broadcast saved." : "Broadcast discarded.");
      setCreds(null);
      setOpen(false);
      setDraftTitle("");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not finish.");
    } finally {
      setBusy(false);
    }
  };

  if (!open && !creds) {
    return (
      <div
        data-testid="mobile-broadcast-panel"
        style={{
          marginTop: 24,
          padding: 22,
          background: "var(--bg-alt)",
          border: "1px dashed var(--border-default)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>From your iPhone · Larix</div>
          <p
            className="editorial-body"
            style={{ fontSize: 14, color: "var(--text-muted)", margin: "6px 0 0 0", fontStyle: "italic" }}
          >
            Broadcasting from outside? Generate a stream key and paste it into Larix.
          </p>
        </div>
        <button
          type="button"
          data-testid="open-mobile-panel"
          onClick={() => setOpen(true)}
          className="btn-secondary"
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          <Smartphone size={14} /> Mobile broadcast
        </button>
      </div>
    );
  }

  if (open && !creds) {
    return (
      <div
        data-testid="mobile-broadcast-setup"
        style={{
          marginTop: 24,
          padding: 28,
          background: "var(--surface)",
          border: "1px solid var(--border-default)",
        }}
      >
        <div className="overline" style={{ color: "var(--brand-accent)" }}>Mobile broadcast · setup</div>
        <h3 className="editorial-h2" style={{ fontSize: 22, marginTop: 6, marginBottom: 18 }}>
          Set the scene.
        </h3>

        <label className="overline">Title</label>
        <input
          className="input-belle"
          data-testid="mobile-title"
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          placeholder="A walk through the marché"
          autoFocus
        />
        <div style={{ marginTop: 18 }}>
          <label className="overline">Room</label>
          <select
            className="input-belle"
            data-testid="mobile-section"
            value={draftSection}
            onChange={(e) => setDraftSection(e.target.value)}
            style={{ width: "100%" }}
          >
            {SECTIONS.map((s) => (
              <option key={s.slug} value={s.slug}>{s.label}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 22, justifyContent: "flex-end" }}>
          <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
          <button
            type="button"
            data-testid="generate-stream-key"
            className="btn-primary"
            onClick={generate}
            disabled={busy}
            style={{ background: "var(--brand-accent)" }}
          >
            {busy ? "Generating…" : "Generate stream key"}
          </button>
        </div>
      </div>
    );
  }

  // creds shown
  return (
    <div
      data-testid="mobile-broadcast-creds"
      style={{
        marginTop: 24,
        padding: 28,
        background: "var(--surface)",
        border: "1px solid var(--brand-accent)",
      }}
    >
      <div className="overline" style={{ color: "var(--brand-accent)" }}>
        <Radio size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} />
        Ready to pair with Larix
      </div>
      <h3 className="editorial-h2" style={{ fontSize: 22, marginTop: 6, marginBottom: 8 }}>
        {creds.title}
      </h3>
      <p className="editorial-body" style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 22, fontStyle: "italic", lineHeight: 1.6 }}>
        Open Larix on your iPhone &rarr; tap the gear icon &rarr; Connections &rarr; + &rarr; paste the values below.
      </p>

      <div style={{ marginBottom: 14, padding: "10px 14px", background: "var(--bg-alt)", border: "1px solid var(--border-default)", fontFamily: "'Cormorant Garamond', serif", fontSize: 14, color: "var(--text-muted)", fontStyle: "italic", lineHeight: 1.5 }}>
        <strong style={{ color: "var(--brand-accent)", fontStyle: "normal" }}>Try Option 1 first.</strong> If Larix says
        the URL is invalid or can&apos;t find the stream, try Option 2 (non-secure RTMP), then Option 3 (separate fields).
      </div>

      <div className="overline" style={{ color: "var(--brand-accent)", marginTop: 6, marginBottom: 8 }}>
        ★ Option 1 · Combined URL (secure RTMPS)
      </div>
      <CredRow
        label="For Larix"
        value={creds.combined_rtmps_url}
        copied={copiedCombined}
        onCopy={() => copyText(creds.combined_rtmps_url, setCopiedCombined)}
        testId="copy-combined-rtmps"
        mask
      />

      <div className="overline" style={{ color: "var(--brand-accent)", marginTop: 18, marginBottom: 8 }}>
        Option 2 · Combined URL (non-secure RTMP)
      </div>
      <CredRow
        label="For Larix"
        value={creds.combined_rtmp_url}
        copied={copiedAlt}
        onCopy={() => copyText(creds.combined_rtmp_url, setCopiedAlt)}
        testId="copy-combined-rtmp"
        mask
      />

      <div className="overline" style={{ color: "var(--brand-accent)", marginTop: 18, marginBottom: 8 }}>
        Option 3 · Separate fields (if Larix has a Stream Key field)
      </div>
      <CredRow
        label="Server URL"
        value={creds.rtmps_url}
        copied={copiedUrl}
        onCopy={() => copyText(creds.rtmps_url, setCopiedUrl)}
        testId="copy-rtmps-url"
      />
      <CredRow
        label="Stream key"
        value={creds.stream_key}
        copied={copiedKey}
        onCopy={() => copyText(creds.stream_key, setCopiedKey)}
        testId="copy-stream-key"
        mask
      />

      <div style={{ marginTop: 24, padding: "16px 18px", background: "var(--bg-alt)", border: "1px solid var(--border-default)" }}>
        <div className="overline" style={{ color: "var(--brand-accent)", marginBottom: 8 }}>Pairing with Larix</div>
        <ol style={{ margin: 0, paddingLeft: 18, fontFamily: "'Cormorant Garamond', serif", fontSize: 16, lineHeight: 1.7, color: "var(--text-primary)" }}>
          <li>In Larix on your iPhone, tap the <strong>gear icon</strong> ⚙️ in the top right.</li>
          <li>Tap <strong>Connections</strong>.</li>
          <li>Tap the <strong>+ icon</strong> in the top-right corner of that screen.</li>
          <li>Set <strong>Name</strong>: <em>Le Salon</em>.</li>
          <li>Set <strong>Mode</strong>: <em>RTMP</em>.</li>
          <li>Set <strong>URL</strong>: paste the <strong>★ Combined URL</strong> above (the long one — it already has the stream key built in).</li>
          <li>Leave <strong>Target Type</strong> as <em>Default</em> (no username or password needed).</li>
          <li>Tap <strong>Save</strong> in the top right.</li>
          <li>Back on Larix&apos;s main camera screen, tap the large <strong>red record button</strong> to start broadcasting.</li>
          <li>When done, tap the red button again to stop, then come back here to save the recording.</li>
        </ol>
      </div>

      <div style={{ display: "flex", gap: 12, marginTop: 24, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid="mobile-discard"
          className="btn-secondary"
          onClick={() => finish(false)}
          disabled={busy}
          style={{ color: "var(--brand-accent)" }}
        >
          Discard
        </button>
        <button
          type="button"
          data-testid="mobile-save"
          className="btn-primary"
          onClick={() => finish(true)}
          disabled={busy}
          style={{ background: "var(--brand-accent)" }}
        >
          {busy ? "Saving…" : "Save broadcast"}
        </button>
      </div>
    </div>
  );
};

const CredRow = ({ label, value, copied, onCopy, testId, mask }) => {
  const [revealed, setRevealed] = useState(!mask);
  const display = revealed ? value : "•".repeat(Math.min(28, value.length));
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        background: "var(--bg-alt)",
        border: "1px solid var(--border-default)",
        marginBottom: 10,
      }}
    >
      <div className="overline" style={{ width: 90, fontSize: 10, color: "var(--text-muted)" }}>
        {label}
      </div>
      <div style={{ flex: 1, fontFamily: "monospace", fontSize: 13, color: "var(--text-primary)", overflowX: "auto", whiteSpace: "nowrap" }}>
        {display}
      </div>
      {mask && (
        <button
          type="button"
          onClick={() => setRevealed((r) => !r)}
          className="overline"
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 10 }}
        >
          {revealed ? "Hide" : "Show"}
        </button>
      )}
      <button
        type="button"
        onClick={onCopy}
        data-testid={testId}
        aria-label={`Copy ${label}`}
        style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6, color: copied ? "var(--brand-accent)" : "var(--text-muted)" }}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
};

export default MobileBroadcastPanel;
